import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API, getAuthHeaders } from '../utils/api';

// VAPID public key - will be generated on backend
const VAPID_PUBLIC_KEY = 'BGScK8qV7RFqZdJQPmqnK9FJUqVJ9QqvO8pBnQcJ8Qy4yKZqXF9Q4vN2xH6L8mC3wT5nJ9K7vF2qR8pL4mN6sW8=';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const PushNotificationManager = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        setIsSubscribed(true);
        setSubscription(subscription);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const subscribeToPushNotifications = async () => {
    setLoading(true);
    
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        toast.error('يجب السماح بالإشعارات أولاً');
        setLoading(false);
        return;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send subscription to backend
      await axios.post(
        `${API}/notifications/subscribe`,
        {
          subscription: subscription.toJSON()
        },
        { withCredentials: true, headers: getAuthHeaders() }
      );

      setIsSubscribed(true);
      setSubscription(subscription);
      toast.success('تم تفعيل الإشعارات الفورية بنجاح!');
      
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      toast.error('فشل تفعيل الإشعارات');
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPushNotifications = async () => {
    setLoading(true);
    
    try {
      if (subscription) {
        await subscription.unsubscribe();
        
        // Notify backend
        await axios.post(
          `${API}/notifications/unsubscribe`,
          {},
          { withCredentials: true, headers: getAuthHeaders() }
        );
      }

      setIsSubscribed(false);
      setSubscription(null);
      toast.success('تم إلغاء الإشعارات الفورية');
      
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('فشل إلغاء الإشعارات');
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {isSubscribed ? (
        <Button
          variant="outline"
          size="sm"
          onClick={unsubscribeFromPushNotifications}
          disabled={loading}
          data-testid="unsubscribe-push-btn"
          className="border-zinc-700 hover:bg-zinc-800"
        >
          <BellOff className="w-4 h-4 mr-2" />
          {loading ? 'جاري الإلغاء...' : 'إيقاف الإشعارات الفورية'}
        </Button>
      ) : (
        <Button
          onClick={subscribeToPushNotifications}
          disabled={loading}
          data-testid="subscribe-push-btn"
          className="bg-blue-600 hover:bg-blue-700 shadow-[0_0_15px_rgba(59,130,246,0.3)]"
        >
          <Bell className="w-4 h-4 mr-2" />
          {loading ? 'جاري التفعيل...' : 'تفعيل الإشعارات الفورية'}
        </Button>
      )}
    </div>
  );
};

export default PushNotificationManager;
